import PropTypes from 'prop-types';
import React, { useState, useMemo, useEffect } from 'react';
import { noop, isEmpty } from 'lodash';
import classnames from 'classnames';
import DetailDescription from 'common/display/SingleItemDetails/dashboard/DetailDescription';
import DetailSummaryForm from 'common/display/SingleItemDetails/DetailSummaryForm';
import usePersonConditions from 'src/common/hooks/usePersonConditions';
import { useFeatureFlag } from 'common/hooks';
import '../stylesheets/detail-view.scss';
import ModalEditForm from '../../../modal/ModalEditForm';
import AssociatedNeedsSection from './AssociatedNeedsSection';

function getReferralClientNeedIds(referral) {
  const personConditionIds = referral?.case?.person_condition_ids;
  if (Array.isArray(personConditionIds) && personConditionIds.length > 0) {
    return personConditionIds;
  }

  return (referral?.case?.person_conditions || [])
    .map((condition) => condition?.id)
    .filter(Boolean);
}

function DetailSummary(props) {
  const {
    className,
    description,
    editable,
    submit,
    descriptionTitle,
    descriptionEditSubtitle,
    descriptionSubtitle,
    item,
    referral,
  } = props;

  const referralClientNeedIds = useMemo(() => getReferralClientNeedIds(referral), [referral]);

  const [currentClientNeedsIds, setCurrentClientNeedsIds] = useState(referralClientNeedIds);

  useEffect(() => {
    setCurrentClientNeedsIds(referralClientNeedIds);
  }, [referralClientNeedIds]);
  const clientNeedFeatureFlag = useFeatureFlag('crtb-2130-multiple-client-needs');
  const personId = referral?.case?.person?.id;
  const clientNeeds = usePersonConditions(personId, false);

  const selectedNeeds = clientNeeds.filter((need) => currentClientNeedsIds?.includes(need.id))
    .map((need) => need.name).join(', ') || '';
  const showClientNeedField = clientNeedFeatureFlag && clientNeeds.length > 0 && !isEmpty(selectedNeeds);

  return (
    <div className={classnames('detail-summary', 'detail-info__summary', className)}>
      <AssociatedNeedsSection
        clientNeeds={clientNeeds}
        editable={editable}
        needIds={currentClientNeedsIds}
        personId={personId}
        referral={referral}
        selectedNeeds={selectedNeeds}
        show={showClientNeedField}
        submit={submit}
        onUpdate={setCurrentClientNeedsIds}
      />
      <div className="row detail-info__description-container">
        <div className="col-md-10">
          <DetailDescription
            id="detail-description-expandable"
            title={descriptionTitle}
            subtitle={descriptionSubtitle}
            description={description}
          />
        </div>
        <div className="col-md-2">
          <ModalEditForm
            id="edit-detail-description-modal"
            buttonId="edit-detail-description"
            cancelButtonId="description-cancel-btn"
            submitButtonId="description-submit-btn"
            header="Edit"
            size="large"
            showEdit={editable}
            labelText="Edit"
          >
            <DetailSummaryForm
              descriptionSubtitle={descriptionEditSubtitle}
              detailObj={item}
              notes={description}
              submit={submit}
            />
          </ModalEditForm>
        </div>
      </div>
    </div>
  );
}

DetailSummary.propTypes = {
  className: PropTypes.string,
  editable: PropTypes.bool,
  description: PropTypes.string,
  descriptionEditSubtitle: PropTypes.node,
  descriptionSubtitle: PropTypes.node,
  descriptionTitle: PropTypes.string,
  item: PropTypes.shape({
    _meta: PropTypes.shape({
      _type: PropTypes.string,
    }),
  }).isRequired,
  submit: PropTypes.func,
  referral: PropTypes.object,
};

DetailSummary.defaultProps = {
  className: '',
  editable: true,
  description: '',
  descriptionEditSubtitle: null,
  descriptionSubtitle: null,
  descriptionTitle: 'Description',
  submit: noop,
  referral: null,
};

export default DetailSummary;
